import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    CalendarCheck,
    CheckCircle2,
    Clock,
    CreditCard,
    MessageCircle,
    Phone,
    Store,
    User,
    X,
} from "lucide-react";
import { DEPOSIT, SITE, waLink } from "../../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingModal({ slot, dateISO, dateLabel, onClose, onBooked }) {
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [payMethod, setPayMethod] = useState("studio");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await axios.post(`${API}/bookings`, {
                slot_id: slot.id,
                date: dateISO,
                name: name.trim(),
                phone: phone.trim(),
                payment_method: payMethod,
            });
            if (payMethod === "studio") {
                setDone(true);
                onBooked?.();
                return;
            }
            const booking = res.data.booking;
            sessionStorage.setItem(
                "kore_pending_booking",
                JSON.stringify({
                    booking_id: booking.id,
                    class_name: slot.class_name,
                    day_label: dateLabel,
                    time: slot.time,
                    name: name.trim(),
                    phone: phone.trim(),
                }),
            );
            const chk = await axios.post(`${API}/payments/checkout`, {
                lookup_key: DEPOSIT.lookupKey,
                origin_url: window.location.origin,
                booking_id: booking.id,
            });
            window.location.href = chk.data.checkout_url;
        } catch (err) {
            toast.error(
                err.response?.data?.detail || "Qualcosa è andato storto. Riprova.",
            );
            setLoading(false);
        }
    };

    const waMessage = waLink(
        `Ciao Kore! Ho appena prenotato online: ${slot.class_name} — ${dateLabel} ore ${slot.time}. Nome: ${name}, Tel: ${phone}.`,
    );

    return (
        <motion.div
            data-testid="booking-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 backdrop-blur-sm sm:items-center sm:p-6"
            onClick={onClose}
        >
            <motion.div
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 40 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                onClick={(e) => e.stopPropagation()}
                className="max-h-[92vh] w-full max-w-md overflow-y-auto border border-hairline bg-white p-7"
            >
                <div className="flex items-start justify-between border-b border-hairline pb-5">
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.26em] text-mute">
                            {done ? "Prenotazione confermata" : "La tua lezione"}
                        </p>
                        <h3 className="mt-2 text-xl font-semibold tracking-tight text-ink">
                            {slot.class_name}
                        </h3>
                        <p className="mt-1.5 flex flex-wrap items-center gap-3 text-sm font-light text-body">
                            <span className="flex items-center gap-1.5">
                                <CalendarCheck className="h-4 w-4 text-ochre" />
                                {dateLabel}
                            </span>
                            <span className="flex items-center gap-1.5">
                                <Clock className="h-4 w-4 text-ochre" />
                                {slot.time} · {slot.duration_min} min
                            </span>
                        </p>
                    </div>
                    <button
                        data-testid="booking-modal-close"
                        onClick={onClose}
                        aria-label="Chiudi"
                        className="flex h-9 w-9 items-center justify-center border border-hairline bg-white text-body transition-colors hover:border-ink hover:text-ink"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {!done ? (
                    <form onSubmit={submit} className="mt-6 space-y-5">
                        <label className="block">
                            <span className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-mute">
                                <User className="h-3.5 w-3.5" /> Nome e cognome
                            </span>
                            <input
                                data-testid="booking-form-name-input"
                                type="text"
                                required
                                minLength={2}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Maria Rossi"
                                className="w-full border border-hairline bg-white px-4 py-3.5 text-base text-ink outline-none transition-colors placeholder:text-mute/60 focus:border-ink"
                            />
                        </label>
                        <label className="block">
                            <span className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-mute">
                                <Phone className="h-3.5 w-3.5" /> Telefono
                            </span>
                            <input
                                data-testid="booking-form-phone-input"
                                type="tel"
                                required
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="+39 333 123 4567"
                                className="w-full border border-hairline bg-white px-4 py-3.5 text-base text-ink outline-none transition-colors placeholder:text-mute/60 focus:border-ink"
                            />
                        </label>

                        <div>
                            <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.22em] text-mute">
                                Come preferisci pagare
                            </span>
                            <div className="grid grid-cols-2 gap-px bg-hairline">
                                <button
                                    type="button"
                                    data-testid="payment-option-studio"
                                    onClick={() => setPayMethod("studio")}
                                    className={`p-4 text-left transition-all duration-200 active:scale-[0.98] ${
                                        payMethod === "studio"
                                            ? "bg-linen outline outline-1 -outline-offset-1 outline-ink"
                                            : "bg-white hover:bg-linen"
                                    }`}
                                >
                                    <Store className={`h-5 w-5 ${payMethod === "studio" ? "text-ink" : "text-mute"}`} />
                                    <p className="mt-2 text-sm font-semibold text-ink">In studio</p>
                                    <p className="mt-0.5 text-xs font-light leading-snug text-mute">
                                        Paghi tutto il giorno della lezione
                                    </p>
                                </button>
                                <button
                                    type="button"
                                    data-testid="payment-option-deposit"
                                    onClick={() => setPayMethod("deposit")}
                                    className={`p-4 text-left transition-all duration-200 active:scale-[0.98] ${
                                        payMethod === "deposit"
                                            ? "bg-linen outline outline-1 -outline-offset-1 outline-ink"
                                            : "bg-white hover:bg-linen"
                                    }`}
                                >
                                    <CreditCard className={`h-5 w-5 ${payMethod === "deposit" ? "text-ink" : "text-mute"}`} />
                                    <p className="mt-2 text-sm font-semibold text-ink">
                                        Caparra {DEPOSIT.label}
                                    </p>
                                    <p className="mt-0.5 text-xs font-light leading-snug text-mute">
                                        Online con carta, il resto in studio
                                    </p>
                                </button>
                            </div>
                        </div>

                        <button
                            data-testid="booking-form-submit-button"
                            type="submit"
                            disabled={loading}
                            className="w-full bg-ink py-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition-all duration-200 hover:bg-charcoal active:scale-[0.98] disabled:opacity-60"
                        >
                            {loading
                                ? payMethod === "deposit"
                                    ? "Reindirizzamento al pagamento…"
                                    : "Invio in corso…"
                                : payMethod === "deposit"
                                  ? `Paga la caparra di ${DEPOSIT.label} e prenota`
                                  : "Conferma prenotazione"}
                        </button>
                        <p className="text-center text-xs font-light text-mute">
                            Nessun account richiesto. Pagamenti sicuri tramite Stripe.
                        </p>
                    </form>
                ) : (
                    <div data-testid="booking-confirmation" className="mt-7 text-center">
                        <CheckCircle2 className="mx-auto h-12 w-12 text-ink" strokeWidth={1.2} />
                        <h4 className="mt-4 text-2xl font-light tracking-tight text-ink">
                            Ti aspettiamo, {name.split(" ")[0]}!
                        </h4>
                        <p className="mt-2 text-sm font-light leading-relaxed text-body">
                            {slot.class_name} · {dateLabel} ore {slot.time}. Il posto è
                            riservato a tuo nome — il pagamento avviene in studio.
                        </p>
                        <div className="mt-6 space-y-3">
                            <a
                                href={waMessage}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="booking-confirmation-whatsapp-button"
                                className="flex w-full items-center justify-center gap-2 bg-ink py-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-white transition-all duration-200 hover:bg-charcoal active:scale-[0.98]"
                            >
                                <MessageCircle className="h-4 w-4" />
                                Invia conferma su WhatsApp
                            </a>
                            <a
                                href={SITE.phoneHref}
                                data-testid="booking-confirmation-call-button"
                                className="flex w-full items-center justify-center gap-2 border border-ink py-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink transition-all duration-200 hover:bg-ink hover:text-white active:scale-[0.98]"
                            >
                                <Phone className="h-4 w-4" />
                                Chiama lo studio
                            </a>
                            <button
                                data-testid="booking-confirmation-done-button"
                                onClick={onClose}
                                className="w-full py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-mute transition-colors hover:text-ink"
                            >
                                Chiudi
                            </button>
                        </div>
                    </div>
                )}
            </motion.div>
        </motion.div>
    );
}
